import { Anchor, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";

import type { MacroTotals, MacroTargetValues } from "./macro-aggregations";
import { MACRO_FIELDS, formatMacroValue } from "./macro-aggregations";
import { MacroTargetCell } from "./MacroTargetCell";

export { DailyBreakdownTable } from "./DailyBreakdownTable";
export { WeeklyBreakdownTable } from "./WeeklyBreakdownTable";
export { MealDetailsAccordion } from "./MealDetailsAccordion";

type StatEntry = {
  label: string;
  value: string;
};

const SummaryItem = ({ label, value }: StatEntry) => (
  <Stack gap={0} key={label} style={{ minWidth: 120 }}>
    <Text
      size="xs"
      tt="uppercase"
      fw={500}
      style={{ letterSpacing: "0.08em", color: "#111827" }}
    >
      {label}
    </Text>
    <Text fw={600} size="lg" style={{ color: "#1F2933" }}>
      {value}
    </Text>
  </Stack>
);

type MacroSummaryGridProps = {
  totals: MacroTotals;
  headlineStats: StatEntry[];
};

export const MacroSummaryGrid = ({ totals, headlineStats }: MacroSummaryGridProps) => {
  return (
    <Stack gap={4}>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="sm">
        {headlineStats.map((entry) => (
          <SummaryItem key={entry.label} {...entry} />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

type MacroTargetsSummaryProps = {
  totals: MacroTotals;
  dayCount: number;
  dailyTargets: MacroTargetValues;
  isLoadingTargets: boolean;
  targetsError: Error | null;
};

const hasAnyTargets = (targets: MacroTargetValues) =>
  MACRO_FIELDS.some((field) => typeof targets[field.key] === "number" && Number.isFinite(targets[field.key] ?? NaN));

export const MacroTargetsSummary = ({
  totals,
  dayCount,
  dailyTargets,
  isLoadingTargets,
  targetsError,
}: MacroTargetsSummaryProps) => {
  const anyTargets = hasAnyTargets(dailyTargets);
  let body: ReactNode;

  if (isLoadingTargets) {
    body = (
      <Text size="sm" style={{ color: "#4B5563" }}>
        Loading daily targets…
      </Text>
    );
  } else if (targetsError) {
    body = (
      <Text size="sm" style={{ color: "#B45309" }}>
        Unable to load targets: {targetsError.message}
      </Text>
    );
  } else if (!anyTargets) {
    body = (
      <Text size="sm" style={{ color: "#4B5563" }}>
        Set your goals in the <Anchor href="/targets">Targets workspace</Anchor> to see guidance here.
      </Text>
    );
  } else if (dayCount === 0) {
    body = (
      <Text size="sm" style={{ color: "#4B5563" }}>
        Log a meal in this range to start comparing against your daily targets.
      </Text>
    );
  } else {
    body = (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        {MACRO_FIELDS.map((field) => {
          const averageValue = totals[field.key] / Math.max(dayCount, 1);
          const targetValue = dailyTargets[field.key] ?? null;

          return (
            <div
              key={field.key}
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: 12,
                padding: "14px 16px",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Stack gap={6}>
                <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
                  {field.label} · avg/day
                </Text>
                <MacroTargetCell
                  actualValue={averageValue}
                  targetValue={targetValue}
                  targetLabel="daily target"
                />
              </Stack>
            </div>
          );
        })}
      </SimpleGrid>
    );
  }

  return (
    <Stack gap={8}>
      <Group justify="space-between" align="center">
        <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
          Daily target check-in
        </Text>
        <Text size="xs" style={{ color: "#6B7280" }}>
          {dayCount === 1 ? "Based on 1 logged day" : `Based on ${dayCount} logged days`}
        </Text>
      </Group>
      {body}
    </Stack>
  );
};
